import * as turf from "@turf/turf"
import type { Feature, FeatureCollection, LineString, Point, Polygon } from "geojson"
import { memo, useCallback, useMemo, useState } from "react"

import {
	DRAW_CIRCLE_LAYER_PAINT,
	DRAW_FILL_LAYER_PAINT,
	DRAW_LINE_LAYER_PAINT,
	LINE_STRING_GEOMETRY_TYPE_FILTER,
	POINT_GEOMETRY_TYPE_FILTER,
	POLYGON_GEOMETRY_TYPE_FILTER,
} from "../constants"
import { MapSlotLayerId } from "../enums"
import { useCurrentMapEvent } from "../hooks"
import type { MapEventHandler } from "../typings"
import { Layer, Source } from "./memo"

const ELLIPSE_DRAWER_SOURCE_ID = "ellipse-drawer-source"

enum EllipseDrawerLayerId {
	CIRCLE = "ellipse-drawer-circle-layer",
	LINE = "ellipse-drawer-line-layer",
	FILL = "ellipse-drawer-fill-layer",
}

interface EllipseDrawerProps {
	onComplete?: (ellipse: Polygon) => void
}

export const EllipseDrawer = memo<EllipseDrawerProps>((props) => {
	const { onComplete } = props

	const [firstPoint, setFirstPoint] = useState<Feature<Point> | null>(null)
	const [secondPoint, setSecondPoint] = useState<Feature<Point> | null>(null)
	const [tempPoint, setTempPoint] = useState<Feature<Point> | null>(null)

	const reset = useCallback(() => {
		setFirstPoint(null)
		setSecondPoint(null)
		setTempPoint(null)
	}, [])

	const handleMapClick = useCallback<MapEventHandler<"click">>(
		(e) => {
			const { lngLat } = e

			const point = turf.point(lngLat.toArray())

			if (!firstPoint) {
				setFirstPoint(point)
			} else if (!secondPoint) {
				setSecondPoint(point)
			} else {
				const bearing = turf.bearing(firstPoint, secondPoint)
				const distance1 = turf.distance(firstPoint, secondPoint)
				const distance2 = turf.distance(firstPoint, point)

				const ellipse = turf.ellipse(firstPoint, distance2, distance1, { angle: bearing })

				onComplete?.(ellipse.geometry)
				reset()
			}
		},
		[firstPoint, onComplete, reset, secondPoint],
	)

	const handleMouseMove = useCallback<MapEventHandler<"mousemove">>(
		(e) => {
			const { lngLat } = e

			const point = turf.point(lngLat.toArray())

			if (!firstPoint) return

			setTempPoint(point)
		},
		[firstPoint],
	)

	useCurrentMapEvent("click", handleMapClick)
	useCurrentMapEvent("mousemove", handleMouseMove)

	const geojson = useMemo<FeatureCollection<Point | Polygon | LineString>>(() => {
		const features: Feature<Polygon | Point | LineString>[] = []

		if (firstPoint) {
			features.push(firstPoint)
		}

		if (secondPoint && !tempPoint) {
			features.push(secondPoint)
		}

		if (!secondPoint && tempPoint) {
			features.push(tempPoint)
		}

		if (firstPoint && !secondPoint && tempPoint) {
			const bearing = turf.bearing(firstPoint, tempPoint)
			const distance1 = turf.distance(firstPoint, tempPoint)
			const oppositePoint = turf.destination(firstPoint, distance1, bearing + 180)
			features.push(oppositePoint)
			features.push(turf.lineString([oppositePoint.geometry.coordinates, tempPoint.geometry.coordinates]))
		}

		if (firstPoint && secondPoint && tempPoint) {
			const bearing = turf.bearing(firstPoint, secondPoint)
			const distance1 = turf.distance(firstPoint, secondPoint)
			const distance2 = turf.distance(firstPoint, tempPoint)

			const ellipse = turf.ellipse(firstPoint, distance2, distance1, { angle: bearing })

			features.push(turf.destination(firstPoint, distance2, bearing + 90))
			features.push(turf.destination(firstPoint, distance2, bearing - 90))
			// @ts-expect-error REASON: sector is a singular Polygon feature
			features.push(turf.polygonToLine(ellipse))
			features.push(ellipse)
		}

		return turf.featureCollection(features)
	}, [firstPoint, secondPoint, tempPoint])

	return (
		<Source id={ELLIPSE_DRAWER_SOURCE_ID} type="geojson" data={geojson}>
			<Layer
				id={EllipseDrawerLayerId.CIRCLE}
				type="circle"
				paint={DRAW_CIRCLE_LAYER_PAINT}
				filter={POINT_GEOMETRY_TYPE_FILTER}
				beforeId={MapSlotLayerId.DRAW}
			/>
			<Layer
				id={EllipseDrawerLayerId.LINE}
				type="line"
				paint={DRAW_LINE_LAYER_PAINT}
				filter={LINE_STRING_GEOMETRY_TYPE_FILTER}
				beforeId={EllipseDrawerLayerId.CIRCLE}
			/>
			<Layer
				id={EllipseDrawerLayerId.FILL}
				type="fill"
				paint={DRAW_FILL_LAYER_PAINT}
				filter={POLYGON_GEOMETRY_TYPE_FILTER}
				beforeId={EllipseDrawerLayerId.LINE}
			/>
		</Source>
	)
})
EllipseDrawer.displayName = "EllipseDrawer"
